import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ClipboardList, Edit, Trash2, Save, X, Plus, ArrowUp, ArrowDown, ChevronDown, ChevronRight, PlusCircle, MinusCircle, AlertTriangle, Filter, ArrowRightLeft, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import ProtocolReviewMode from "./ProtocolReviewMode";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { value: "all", label: "All Services" },
  { value: "ground", label: "PCP/ACP-G" },
  { value: "sfa_emr", label: "SFA/EMR" },
  { value: "wes", label: "PCP-PS" },
  { value: "air_adult", label: "Air Ambulance – Adult" },
  { value: "air_pediatric", label: "Air Ambulance – OB/NEO/PEDS" },
];

const SERVICE_LABELS = { ground: "PCP/ACP-G", sfa_emr: "SFA/EMR", wes: "PCP-PS", air_adult: "Air Adult", air_pediatric: "OB/NEO/PEDS" };
const SERVICE_COLORS = { ground: "bg-blue-600", sfa_emr: "bg-emerald-600", wes: "bg-amber-600", air_adult: "bg-cyan-600", air_pediatric: "bg-pink-600" };

const CARE_LEVEL_CONFIG = {
  all_responders: { name: "All Responders", color: "border-l-gray-400 bg-gray-500/5" },
  sfa: { name: "Standard First Aid (SFA)", color: "border-l-sky-400 bg-sky-400/5" },
  emr: { name: "Emergency Medical Responder (EMR)", color: "border-l-emerald-700 bg-emerald-700/5" },
  pcp: { name: "Primary Care Paramedic (PCP)", color: "border-l-blue-600 bg-blue-600/5" },
  pcp_es: { name: "PCP – Extended Scope", color: "border-l-purple-500 bg-purple-500/5" },
  "pcp-es": { name: "PCP – Extended Scope", color: "border-l-purple-500 bg-purple-500/5" },
  wes: { name: "Paramedic Specialist (PCP-PS)", color: "border-l-orange-500 bg-orange-500/5" },
  paramedic_specialist: { name: "Paramedic Specialist", color: "border-l-orange-500 bg-orange-500/5" },
  acp: { name: "Advanced Care Paramedic (ACP)", color: "border-l-yellow-500 bg-yellow-500/5" },
  acp_g: { name: "Advanced Care Paramedic – Ground (ACP-G)", color: "border-l-yellow-500 bg-yellow-500/5" },
  acp_f: { name: "Advanced Care Paramedic – Flight (ACP-F)", color: "border-l-green-400 bg-green-400/5" },
  ccp: { name: "Critical Care Paramedic (CCP)", color: "border-l-red-500 bg-red-500/5" },
};

const CARE_LEVEL_IDS = Object.keys(CARE_LEVEL_CONFIG);

// Move Intervention Dialog
const MoveInterventionDialog = ({ open, onClose, onMove, currentLevel, careLevels }) => {
  if (!open) return null;
  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5" /> Move to Care Level
          </AlertDialogTitle>
          <AlertDialogDescription>Select the care level to move this intervention to:</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 py-2">
          {careLevels.filter((_, i) => i !== currentLevel).map((cl, i) => (
            <Button key={i} variant="outline" className="w-full justify-start" onClick={() => onMove(careLevels.indexOf(cl))}>
              {CARE_LEVEL_CONFIG[cl.level]?.name || cl.title}
            </Button>
          ))}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Protocol Editor Component
const ProtocolEditor = ({ protocol, setProtocol, onSave, onCancel, categories = [] }) => {
  const [saving, setSaving] = useState(false);
  const [expandedLevels, setExpandedLevels] = useState(() => {
    const initial = {};
    (protocol.care_levels || []).forEach((_, i) => { initial[i] = true; });
    return initial;
  });
  const [expandedInfoBoxes, setExpandedInfoBoxes] = useState(true);
  const [moveDialog, setMoveDialog] = useState({ open: false, levelIndex: null, intIndex: null });

  const toggleLevel = (i) => setExpandedLevels(p => ({...p, [i]: !p[i]}));
  const handleSave = async () => { setSaving(true); await onSave(); setSaving(false); };
  const updateName = (v) => setProtocol({...protocol, name: v});
  const updateCategory = (v) => setProtocol({...protocol, category: v});
  const updateId = (v) => setProtocol({...protocol, id: v.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')});

  // Info Box handlers
  const updateInfoBox = (i, f, v) => { const b = [...(protocol.info_boxes || [])]; b[i] = {...b[i], [f]: v}; setProtocol({...protocol, info_boxes: b}); };
  const addInfoBox = () => setProtocol({...protocol, info_boxes: [...(protocol.info_boxes || []), { type: "note", title: "New Note", content: "" }]});
  const removeInfoBox = (i) => setProtocol({...protocol, info_boxes: (protocol.info_boxes || []).filter((_, j) => j !== i)});
  const moveInfoBox = (i, d) => { const b = [...(protocol.info_boxes || [])]; const n = i + d; if (n < 0 || n >= b.length) return; [b[i], b[n]] = [b[n], b[i]]; setProtocol({...protocol, info_boxes: b}); };

  // Care Level handlers
  const addCareLevel = () => {
    const existing = (protocol.care_levels || []).map(cl => cl.level);
    const available = CARE_LEVEL_IDS.filter(id => !existing.includes(id));
    if (!available.length) { toast.error("All care levels already added"); return; }
    const newLevel = available[0];
    const newLevels = [...(protocol.care_levels || []), { level: newLevel, title: CARE_LEVEL_CONFIG[newLevel]?.name || newLevel, interventions: [] }];
    setProtocol({...protocol, care_levels: newLevels});
    setExpandedLevels(p => ({...p, [newLevels.length - 1]: true}));
  };

  const removeCareLevel = (idx) => {
    if ((protocol.care_levels || []).length <= 1) { toast.error("Must have at least one care level"); return; }
    const newLevels = (protocol.care_levels || []).filter((_, i) => i !== idx);
    setProtocol({...protocol, care_levels: newLevels});
  };

  const updateCareLevelType = (idx, newLevelId) => {
    const newLevels = [...(protocol.care_levels || [])];
    newLevels[idx] = { ...newLevels[idx], level: newLevelId, title: CARE_LEVEL_CONFIG[newLevelId]?.name || newLevelId };
    setProtocol({...protocol, care_levels: newLevels});
  };

  // Intervention handlers
  const updateIntervention = (li, ii, f, v) => { const l = [...protocol.care_levels]; l[li].interventions[ii] = {...l[li].interventions[ii], [f]: v}; setProtocol({...protocol, care_levels: l}); };
  const addIntervention = (li) => { const l = [...protocol.care_levels]; l[li].interventions.push({ text: "", sub_steps: [] }); setProtocol({...protocol, care_levels: l}); };
  const removeIntervention = (li, ii) => { const l = [...protocol.care_levels]; l[li].interventions = l[li].interventions.filter((_, i) => i !== ii); setProtocol({...protocol, care_levels: l}); };
  const moveIntervention = (li, ii, d) => { const l = [...protocol.care_levels]; const arr = l[li].interventions; const n = ii + d; if (n < 0 || n >= arr.length) return; [arr[ii], arr[n]] = [arr[n], arr[ii]]; setProtocol({...protocol, care_levels: l}); };

  const moveInterventionToLevel = (targetLevelIndex) => {
    const { levelIndex, intIndex } = moveDialog;
    const l = [...protocol.care_levels];
    const [moved] = l[levelIndex].interventions.splice(intIndex, 1);
    l[targetLevelIndex].interventions.push(moved);
    setProtocol({...protocol, care_levels: l});
    setMoveDialog({ open: false, levelIndex: null, intIndex: null });
    toast.success("Intervention moved");
  };

  // Sub-step handlers
  const updateSubStep = (li, ii, si, v) => { const l = [...protocol.care_levels]; l[li].interventions[ii].sub_steps[si] = v; setProtocol({...protocol, care_levels: l}); };
  const addSubStep = (li, ii) => { const l = [...protocol.care_levels]; if (!l[li].interventions[ii].sub_steps) l[li].interventions[ii].sub_steps = []; l[li].interventions[ii].sub_steps.push(""); setProtocol({...protocol, care_levels: l}); };
  const removeSubStep = (li, ii, si) => { const l = [...protocol.care_levels]; l[li].interventions[ii].sub_steps = l[li].interventions[ii].sub_steps.filter((_, i) => i !== si); setProtocol({...protocol, care_levels: l}); };

  return (
    <div className="min-h-screen bg-background">
      <MoveInterventionDialog
        open={moveDialog.open}
        onClose={() => setMoveDialog({ open: false, levelIndex: null, intIndex: null })}
        onMove={moveInterventionToLevel}
        currentLevel={moveDialog.levelIndex}
        careLevels={protocol.care_levels || []}
      />

      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onCancel}><X className="w-4 h-4 mr-1" />Back</Button>
            <h1 className="text-lg font-bold flex items-center gap-2"><Edit className="w-5 h-5" />{protocol._isNew ? "New Protocol" : protocol.name}</h1>
            {protocol.service && <Badge className={`${SERVICE_COLORS[protocol.service] || "bg-zinc-600"} text-white text-xs`}>{SERVICE_LABELS[protocol.service] || protocol.service}</Badge>}
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-protocol-btn">
            <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Protocol Details */}
        <Card className="mb-6">
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block text-muted-foreground">Protocol Name</label>
                <Input value={protocol.name} onChange={(e) => updateName(e.target.value)} className="text-lg font-medium" data-testid="protocol-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block text-muted-foreground">Category</label>
                <select value={protocol.category} onChange={(e) => updateCategory(e.target.value)} className="w-full px-3 py-2 rounded border bg-background text-sm">
                  {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name} ({SERVICE_LABELS[cat.service] || cat.service})</option>)}
                </select>
              </div>
            </div>
            {protocol._isNew && (
              <div>
                <label className="text-sm font-medium mb-1 block text-muted-foreground">Protocol ID (URL slug)</label>
                <Input value={protocol.id} onChange={(e) => updateId(e.target.value)} placeholder="e.g. my-new-protocol" className="font-mono text-sm" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Care Levels */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Care Levels</h2>
          <Button size="sm" variant="outline" onClick={addCareLevel} data-testid="add-care-level-btn">
            <Plus className="w-4 h-4 mr-1" />Add Care Level
          </Button>
        </div>

        {(protocol.care_levels || []).map((level, levelIndex) => {
          const cfg = CARE_LEVEL_CONFIG[level.level] || { name: level.title, color: "border-l-gray-400" };
          return (
            <Card key={levelIndex} className={`mb-4 border-l-4 ${cfg.color}`}>
              <CardHeader className="py-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer" onClick={() => toggleLevel(levelIndex)}>
                    {expandedLevels[levelIndex] ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                    <select
                      value={level.level}
                      onChange={(e) => { e.stopPropagation(); updateCareLevelType(levelIndex, e.target.value); }}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-transparent border border-zinc-700 rounded px-2 py-1 text-sm font-medium"
                    >
                      {CARE_LEVEL_IDS.map(id => <option key={id} value={id}>{CARE_LEVEL_CONFIG[id]?.name || id}</option>)}
                    </select>
                    <Badge variant="outline" className="text-xs shrink-0">{level.interventions?.length || 0}</Badge>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addIntervention(levelIndex)}><Plus className="w-3 h-3 mr-1" />Step</Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => removeCareLevel(levelIndex)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              </CardHeader>
              {expandedLevels[levelIndex] && (
                <CardContent className="pt-0 space-y-2">
                  {(level.interventions || []).map((intervention, intIndex) => (
                    <div key={intIndex} className="border rounded p-3 bg-card/50 group">
                      <div className="flex items-start gap-1.5">
                        <div className="flex flex-col gap-0.5 pt-1.5">
                          <button className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted disabled:opacity-30" onClick={() => moveIntervention(levelIndex, intIndex, -1)} disabled={intIndex === 0}><ArrowUp className="w-3 h-3" /></button>
                          <button className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted disabled:opacity-30" onClick={() => moveIntervention(levelIndex, intIndex, 1)} disabled={intIndex === (level.interventions?.length || 0) - 1}><ArrowDown className="w-3 h-3" /></button>
                        </div>
                        <span className="text-muted-foreground text-xs pt-2.5 w-5 text-right shrink-0">{intIndex + 1}.</span>
                        <textarea
                          value={intervention.text}
                          onChange={(e) => updateIntervention(levelIndex, intIndex, 'text', e.target.value)}
                          className="flex-1 bg-transparent border border-transparent hover:border-zinc-700 focus:border-zinc-600 rounded px-2 py-1.5 text-sm resize-none min-h-[36px] focus:outline-none focus:ring-1 focus:ring-ring"
                          rows={Math.max(1, Math.ceil(intervention.text.length / 80))}
                          data-testid={`intervention-${levelIndex}-${intIndex}`}
                        />
                        <div className="flex gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted" title="Move to another care level" onClick={() => setMoveDialog({ open: true, levelIndex, intIndex })}><ArrowRightLeft className="w-3 h-3" /></button>
                          <button className="h-7 w-7 flex items-center justify-center rounded hover:bg-destructive/20 text-destructive" onClick={() => removeIntervention(levelIndex, intIndex)}><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>
                      {/* Sub-steps */}
                      {intervention.sub_steps && intervention.sub_steps.length > 0 && (
                        <div className="ml-12 mt-2 space-y-1.5">
                          {intervention.sub_steps.map((step, subIndex) => (
                            <div key={subIndex} className="flex items-center gap-1.5 group/sub">
                              <span className="text-muted-foreground text-xs">•</span>
                              <Input value={step} onChange={(e) => updateSubStep(levelIndex, intIndex, subIndex, e.target.value)} className="flex-1 h-8 text-sm" />
                              <button className="h-6 w-6 flex items-center justify-center rounded hover:bg-destructive/20 text-destructive opacity-0 group-hover/sub:opacity-100" onClick={() => removeSubStep(levelIndex, intIndex, subIndex)}><MinusCircle className="w-3 h-3" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                      <button className="ml-12 mt-1 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" onClick={() => addSubStep(levelIndex, intIndex)}>
                        <PlusCircle className="w-3 h-3" />sub-step
                      </button>
                    </div>
                  ))}
                  {(!level.interventions || level.interventions.length === 0) && (
                    <div className="text-center py-4">
                      <p className="text-muted-foreground text-sm mb-2">No interventions</p>
                      <Button size="sm" variant="outline" onClick={() => addIntervention(levelIndex)}><Plus className="w-3 h-3 mr-1" />Add First Intervention</Button>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}

        {/* Info Boxes */}
        <div className="flex items-center justify-between mb-4 mt-8">
          <h2 className="text-lg font-semibold cursor-pointer flex items-center gap-2" onClick={() => setExpandedInfoBoxes(!expandedInfoBoxes)}>
            {expandedInfoBoxes ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            Notes & Warnings ({protocol.info_boxes?.length || 0})
          </h2>
          <Button size="sm" variant="outline" onClick={addInfoBox}><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
        {expandedInfoBoxes && (
          <div className="space-y-3 mb-8">
            {(protocol.info_boxes || []).map((box, index) => (
              <Card key={index} className={`border-l-4 ${box.type === 'warning' ? 'border-l-amber-500' : box.type === 'critical' ? 'border-l-red-500' : 'border-l-blue-500'}`}>
                <CardContent className="py-3">
                  <div className="flex items-start gap-2">
                    <div className="flex flex-col gap-0.5 pt-1">
                      <button className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted disabled:opacity-30" onClick={() => moveInfoBox(index, -1)} disabled={index === 0}><ArrowUp className="w-3 h-3" /></button>
                      <button className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted disabled:opacity-30" onClick={() => moveInfoBox(index, 1)} disabled={index === (protocol.info_boxes?.length || 0) - 1}><ArrowDown className="w-3 h-3" /></button>
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex gap-2">
                        <select value={box.type} onChange={(e) => updateInfoBox(index, 'type', e.target.value)} className="px-2 py-1 rounded border bg-background text-xs">
                          <option value="note">Note</option>
                          <option value="warning">Warning</option>
                          <option value="critical">Critical</option>
                        </select>
                        <Input value={box.title} onChange={(e) => updateInfoBox(index, 'title', e.target.value)} className="flex-1 h-8 text-sm font-medium" />
                      </div>
                      <textarea value={box.content} onChange={(e) => updateInfoBox(index, 'content', e.target.value)} className="w-full min-h-[60px] p-2 rounded border bg-background text-sm" rows={3} />
                    </div>
                    <button className="h-7 w-7 flex items-center justify-center rounded hover:bg-destructive/20 text-destructive" onClick={() => removeInfoBox(index)}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {(!protocol.info_boxes || protocol.info_boxes.length === 0) && <p className="text-muted-foreground text-center py-4 text-sm">No notes or warnings.</p>}
          </div>
        )}

        <div className="flex justify-between items-center pt-4 border-t sticky bottom-0 bg-background py-4">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-protocol-btn-bottom">
            <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Admin Protocols Management
const AdminProtocols = () => {
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [editingProtocol, setEditingProtocol] = useState(null);
  const [reviewMode, setReviewMode] = useState(false);
  const [categories, setCategories] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: "" });
  const { token } = useAuth();

  const fetchProtocols = async () => {
    try {
      const res = await axios.get(`${API}/protocols`);
      setProtocols(res.data);
    } catch (error) {
      toast.error("Failed to load protocols");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error("Failed to load categories");
    }
  };

  useEffect(() => { fetchProtocols(); fetchCategories(); }, []);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/protocols/${deleteDialog.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setProtocols(protocols.filter(p => p.id !== deleteDialog.id));
      toast.success("Protocol deleted");
    } catch (error) {
      toast.error("Failed to delete protocol");
    } finally {
      setDeleteDialog({ open: false, id: null, name: "" });
    }
  };

  const handleEdit = (protocol) => setEditingProtocol(JSON.parse(JSON.stringify(protocol)));

  const handleSave = async () => {
    try {
      if (editingProtocol._isNew) {
        await axios.post(`${API}/admin/protocols`, editingProtocol, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Protocol created!");
      } else {
        await axios.put(`${API}/admin/protocols/${editingProtocol.id}`, editingProtocol, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Protocol saved!");
      }
      setEditingProtocol(null);
      fetchProtocols();
    } catch (error) {
      toast.error(editingProtocol._isNew ? "Failed to create" : "Failed to save");
    }
  };

  const handleCreateNew = () => {
    setEditingProtocol({
      id: `custom-protocol-${Date.now()}`,
      name: "New Protocol",
      category: categories[0]?.id || "general",
      service: serviceFilter !== "all" ? serviceFilter : "ground",
      info_boxes: [],
      care_levels: [{ level: "pcp", title: "Primary Care Paramedic (PCP)", interventions: [{ text: "", sub_steps: [] }] }],
      _isNew: true
    });
  };

  const filtered = protocols
    .filter(p => serviceFilter === "all" || p.service === serviceFilter)
    .filter(p => (p.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || (p.category || '').toLowerCase().includes(searchQuery.toLowerCase()));

  // Group by category for the filtered view
  const grouped = {};
  filtered.forEach(p => {
    const cat = categories.find(c => c.id === p.category);
    const catName = cat?.name || p.category || "Uncategorized";
    if (!grouped[catName]) grouped[catName] = [];
    grouped[catName].push(p);
  });

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading protocols...</div>;
  if (editingProtocol) return <ProtocolEditor protocol={editingProtocol} setProtocol={setEditingProtocol} onSave={handleSave} onCancel={() => setEditingProtocol(null)} categories={categories} />;
  if (reviewMode) return <ProtocolReviewMode protocolList={filtered} onExit={() => { setReviewMode(false); fetchProtocols(); }} categories={categories} token={token} onProtocolsChanged={fetchProtocols} />;

  return (
    <div className="min-h-screen bg-background">
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, id: null, name: "" })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-destructive" />Delete Protocol</AlertDialogTitle>
            <AlertDialogDescription>Delete <strong>"{deleteDialog.name}"</strong>? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />Protocols
            <Badge variant="outline">{filtered.length} / {protocols.length}</Badge>
          </h1>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setReviewMode(true)} disabled={filtered.length === 0} data-testid="review-mode-btn">
              <BookOpen className="w-4 h-4 mr-1" />Review ({filtered.length})
            </Button>
            <Button size="sm" onClick={handleCreateNew} className="bg-green-600 hover:bg-green-700"><Plus className="w-4 h-4 mr-1" />New</Button>
            <Link to="/admin/dashboard"><Button variant="outline" size="sm">Dashboard</Button></Link>
          </div>
        </div>
        <div className="container mx-auto px-4 pb-3 flex gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="bg-background border rounded px-3 py-1.5 text-sm"
              data-testid="admin-service-filter"
            >
              {SERVICE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <Input
            placeholder="Search by name or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-sm h-9"
            data-testid="admin-protocol-search"
          />
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {Object.entries(grouped).map(([catName, protos]) => (
          <div key={catName} className="mb-6">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
              {catName} <Badge variant="outline" className="text-xs">{protos.length}</Badge>
            </h3>
            <div className="space-y-1.5">
              {protos.map(protocol => (
                <Card key={protocol.id} className="hover:border-zinc-600 transition-colors">
                  <CardContent className="py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{protocol.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`${SERVICE_COLORS[protocol.service] || "bg-zinc-600"} text-white text-[10px] px-1.5 py-0`}>
                          {SERVICE_LABELS[protocol.service] || protocol.service}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {(protocol.care_levels || []).map(cl => CARE_LEVEL_CONFIG[cl.level]?.name?.split('(')[1]?.replace(')', '') || cl.title).join(' → ')}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <Link to={`/protocol/${protocol.id}`}><Button variant="outline" size="sm" className="h-8 text-xs">View</Button></Link>
                      <Button variant="secondary" size="sm" className="h-8 text-xs" onClick={() => handleEdit(protocol)} data-testid={`edit-${protocol.id}`}>
                        <Edit className="w-3 h-3 mr-1" />Edit
                      </Button>
                      <Button variant="destructive" size="sm" className="h-8 w-8 p-0" onClick={() => setDeleteDialog({ open: true, id: protocol.id, name: protocol.name })}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p>No protocols found for this filter.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProtocols;
